import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Disponibilidad from './Disponibilidad';

const API = process.env.REACT_APP_API_URL || '';

const estiloTabla = {
  bgcolor: 'var(--c-superficie)',
  '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' }
};
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#e0e0e0',
    '& fieldset': { borderColor: 'var(--c-primario)' },
    '&:hover fieldset': { borderColor: 'var(--c-acento)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--c-acento)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--c-acento)' },
  '& .MuiSelect-icon': { color: 'var(--c-acento)' },
};
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };
const inputStyle = { style: { color: '#e0e0e0' } };
const selectMenuProps = { PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } };

const ESTADO_MEDICO = [
  { value: 'activo',      label: 'Activo',      color: 'success' },
  { value: 'inactivo',    label: 'Inactivo',    color: 'default' },
  { value: 'vacaciones',  label: 'Vacaciones',  color: 'warning' },
  { value: 'licencia',    label: 'Licencia',    color: 'error' },
];

const formVacio = {
  nombre: '', especialidad_id: '', matricula: '', dni: '', cuit: '',
  email: '', celular: '', domicilio: '', descripcion: '', estado: 'activo', color: '#1565c0',
};

export default function Medicos() {
  const [medicos, setMedicos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVacio);
  const [dispOpen, setDispOpen] = useState(false);
  const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // ── Gestión de usuario del médico ──
  const [usuarioOpen, setUsuarioOpen] = useState(false);
  const [usuarioMedico, setUsuarioMedico] = useState(null); // médico seleccionado
  const [usuarioExistente, setUsuarioExistente] = useState(null); // staff existente o null
  const [usuarioForm, setUsuarioForm] = useState({ nombre: '', email: '', password: '' });
  const [usuarioGuardando, setUsuarioGuardando] = useState(false);
  const [usuarioError, setUsuarioError] = useState('');
  const [usuarioExito, setUsuarioExito] = useState('');

  useEffect(() => { cargarTodo(); }, []);

  async function cargarTodo() {
    try {
      const [medicosR, espR] = await Promise.all([
        axios.get(`${API}/api/medicos`),
        axios.get(`${API}/api/especialidades`),
      ]);
      setMedicos(medicosR.data);
      setEspecialidades(espR.data);
    } catch (err) { console.error(err); }
  }

  function abrirNuevo() {
    setEditando(null);
    setForm(formVacio);
    setError('');
    setDialogOpen(true);
  }

  function abrirEditar(m) {
    setEditando(m.id);
    setForm({
      nombre: m.nombre || '',
      especialidad_id: m.especialidad_id || '',
      matricula: m.matricula || '',
      dni: m.dni || '',
      cuit: m.cuit || '',
      email: m.email || '',
      celular: m.celular || '',
      domicilio: m.domicilio || '',
      descripcion: m.descripcion || '',
      estado: m.estado || 'activo',
      color: m.color || '#1565c0',
    });
    setError('');
    setDialogOpen(true);
  }

  async function guardar() {
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setGuardando(true);
    setError('');
    try {
      if (editando) {
        await axios.put(`${API}/api/medicos/${editando}`, form);
      } else {
        await axios.post(`${API}/api/medicos`, form);
      }
      setDialogOpen(false);
      cargarTodo();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  async function abrirUsuario(m) {
    setUsuarioMedico(m);
    setUsuarioError('');
    setUsuarioExito('');
    setUsuarioForm({ nombre: m.nombre, email: m.email || '', password: '' });
    setUsuarioExistente(null);
    setUsuarioOpen(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${m.id}/usuario`);
      setUsuarioExistente(r.data); // null si no tiene
      if (r.data) {
        setUsuarioForm({ nombre: r.data.nombre, email: r.data.email, password: '' });
      }
    } catch (err) { console.error(err); }
  }

  async function guardarUsuario() {
    if (!usuarioForm.email.trim()) { setUsuarioError('El email es obligatorio'); return; }
    if (!usuarioExistente && !usuarioForm.password.trim()) { setUsuarioError('La contraseña es obligatoria'); return; }
    setUsuarioGuardando(true);
    setUsuarioError('');
    setUsuarioExito('');
    try {
      if (usuarioExistente) {
        // Actualizar usuario existente
        const payload = { nombre: usuarioForm.nombre, email: usuarioForm.email, rol: 'medico', medico_id: usuarioMedico.id };
        if (usuarioForm.password.trim()) payload.password = usuarioForm.password;
        await axios.put(`${API}/api/staff/${usuarioExistente.id}`, payload);
        setUsuarioExito('Usuario actualizado correctamente.');
      } else {
        // Crear nuevo usuario
        await axios.post(`${API}/api/staff`, {
          nombre: usuarioForm.nombre,
          email: usuarioForm.email,
          password: usuarioForm.password,
          rol: 'medico',
          medico_id: usuarioMedico.id,
        });
        setUsuarioExito('Usuario creado correctamente.');
      }
      // Recargar para mostrar estado actualizado
      const r = await axios.get(`${API}/api/medicos/${usuarioMedico.id}/usuario`);
      setUsuarioExistente(r.data);
      setUsuarioForm(f => ({ ...f, password: '' }));
    } catch (err) {
      setUsuarioError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setUsuarioGuardando(false);
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este médico?')) return;
    await axios.delete(`${API}/api/medicos/${id}`);
    cargarTodo();
  }

  function estadoChip(estado) {
    const e = ESTADO_MEDICO.find(x => x.value === estado) || ESTADO_MEDICO[0];
    return <Chip label={e.label} color={e.color} size="small" />;
  }

  const f = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
    InputLabelProps: labelStyle,
    inputProps: inputStyle,
    sx: fieldSx,
    fullWidth: true,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Gestión de Médicos
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
          Nuevo Médico
        </Button>
      </Box>

      <TableContainer component={Paper} sx={estiloTabla}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
              <TableCell>Color</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Matrícula</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Celular</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {medicos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: '#b0b0b0' }}>
                  No hay médicos registrados
                </TableCell>
              </TableRow>
            ) : medicos.map(m => (
              <TableRow key={m.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                <TableCell>
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: m.color }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {m.nombre}
                  {m.dni && <Typography sx={{ fontSize: 11, color: 'var(--c-acento)' }}>DNI: {m.dni}</Typography>}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{m.matricula || '—'}</TableCell>
                <TableCell>
                  {m.especialidad_nombre
                    ? <Chip label={m.especialidad_nombre} size="small" sx={{ bgcolor: 'var(--c-primario)', color: 'var(--c-acento)' }} />
                    : <Typography sx={{ color: '#666', fontSize: 13 }}>—</Typography>}
                </TableCell>
                <TableCell>{m.email || '—'}</TableCell>
                <TableCell>{m.celular || '—'}</TableCell>
                <TableCell>{estadoChip(m.estado)}</TableCell>
                <TableCell>
                  <IconButton onClick={() => abrirEditar(m)} sx={{ color: 'var(--c-acento)' }} size="small" title="Editar">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => eliminar(m.id)} sx={{ color: '#f48fb1' }} size="small" title="Eliminar">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => { setMedicoSeleccionado(m); setDispOpen(true); }}
                    sx={{ color: '#81c784' }} size="small" title="Disponibilidad">
                    <ScheduleIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => abrirUsuario(m)}
                    sx={{ color: '#ce93d8' }} size="small" title="Gestionar usuario">
                    <ManageAccountsIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)', fontWeight: 700 }}>
          {editando ? 'Editar Médico' : 'Nuevo Médico'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            {/* Fila 1: Nombre + Matrícula */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
              <TextField label="Nombre completo *" {...f('nombre')} />
              <TextField label="Matrícula" {...f('matricula')} />
            </Box>

            {/* Fila 2: DNI + CUIT */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="DNI" {...f('dni')} />
              <TextField label="CUIT" {...f('cuit')} />
            </Box>

            {/* Fila 3: Email + Celular */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="Email" type="email" {...f('email')} />
              <TextField label="Celular" {...f('celular')} />
            </Box>

            {/* Fila 4: Especialidad + Estado */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Especialidad</InputLabel>
                <Select value={form.especialidad_id} label="Especialidad"
                  onChange={e => setForm({ ...form, especialidad_id: e.target.value })}
                  MenuProps={selectMenuProps}>
                  <MenuItem value=""><em style={{ color: '#b0b0b0' }}>Sin especialidad</em></MenuItem>
                  {especialidades.map(e => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Estado</InputLabel>
                <Select value={form.estado} label="Estado"
                  onChange={e => setForm({ ...form, estado: e.target.value })}
                  MenuProps={selectMenuProps}>
                  {ESTADO_MEDICO.map(e => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {/* Fila 5: Domicilio */}
            <TextField label="Domicilio profesional" {...f('domicilio')} />

            {/* Fila 6: Descripción */}
            <TextField label="Descripción / Presentación profesional" {...f('descripcion')}
              multiline rows={3} />

            {/* Fila 7: Color */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ color: 'var(--c-acento)', fontSize: 14 }}>Color en agenda:</Typography>
              <select value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                style={{ background: 'var(--c-superficie)', color: '#e0e0e0', border: '1px solid var(--c-primario)', borderRadius: 4, padding: '6px 10px' }}>
                <option value="#1565c0">Azul</option>
                <option value="#2e7d32">Verde</option>
                <option value="#ad1457">Rosa</option>
                <option value="#e65100">Naranja</option>
                <option value="#6a1b9a">Violeta</option>
                <option value="#00695c">Verde oscuro</option>
                <option value="#c62828">Rojo</option>
                <option value="#37474f">Gris</option>
                <option value="#f57f17">Amarillo</option>
                <option value="#0277bd">Celeste</option>
              </select>
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: form.color, border: '2px solid rgba(255,255,255,0.2)' }} />
            </Box>

          </Box>
          {error && <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1.5 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog gestionar usuario del médico ── */}
      <Dialog open={usuarioOpen} onClose={() => setUsuarioOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)', fontWeight: 700 }}>
          {usuarioExistente ? 'Modificar usuario' : 'Crear usuario'}
          {usuarioMedico && (
            <Typography sx={{ fontSize: 13, color: '#90a4ae', mt: 0.3 }}>
              {usuarioMedico.nombre}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {usuarioExistente ? (
            <Typography sx={{ fontSize: 12, color: '#66bb6a', bgcolor: 'rgba(102,187,106,0.1)', p: 1.5, borderRadius: 1, border: '1px solid #2e7d32' }}>
              ✓ Este médico ya tiene usuario activo
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#ffb74d', bgcolor: 'rgba(255,183,77,0.1)', p: 1.5, borderRadius: 1, border: '1px solid #e65100' }}>
              Este médico aún no tiene usuario para ingresar al sistema
            </Typography>
          )}

          <TextField label="Nombre completo" fullWidth value={usuarioForm.nombre}
            onChange={e => setUsuarioForm(f => ({ ...f, nombre: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />

          <TextField label="Email *" type="email" fullWidth value={usuarioForm.email}
            onChange={e => setUsuarioForm(f => ({ ...f, email: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />

          <TextField
            label={usuarioExistente ? 'Nueva contraseña (dejá vacío para no cambiar)' : 'Contraseña *'}
            type="password" fullWidth value={usuarioForm.password}
            onChange={e => setUsuarioForm(f => ({ ...f, password: e.target.value }))}
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />

          {usuarioError && (
            <Typography sx={{ color: '#f44336', fontSize: 13 }}>{usuarioError}</Typography>
          )}
          {usuarioExito && (
            <Typography sx={{ color: '#66bb6a', fontSize: 13 }}>{usuarioExito}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsuarioOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cerrar</Button>
          <Button onClick={guardarUsuario} variant="contained" disabled={usuarioGuardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {usuarioGuardando ? 'Guardando...' : usuarioExistente ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </DialogActions>
      </Dialog>

      {dispOpen && medicoSeleccionado && (
        <Disponibilidad
          medicoId={medicoSeleccionado.id}
          medicoNombre={medicoSeleccionado.nombre}
          onClose={() => { setDispOpen(false); setMedicoSeleccionado(null); }}
        />
      )}
    </Box>
  );
}
